/**
 * The app's own sign-in page lives at its own URL. A signed-out user on any
 * other page is moved there, with the page they asked for kept in
 * `?returnTo=`, and comes back to it once signed in.
 *
 * There is no router above `<Authenticate>` (each app builds its own inside
 * it), so this works on `window.location` and `history` directly.
 */

export const LOGIN_PATH = '/login';

const RETURN_TO_PARAM = 'returnTo';

const currentPath = () =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

export const isLoginPage = () => window.location.pathname === LOGIN_PATH;

/**
 * Cognito's hosted-UI callback (`?code=&state=`, or `?error=&state=`), which
 * the AuthProvider redeems. Its code is single-use, so it must never be kept
 * as a page to come back to.
 */
export const isOidcCallback = () => {
  const params = new URLSearchParams(window.location.search);
  return (params.has('code') || params.has('error')) && params.has('state');
};

/** Only ever a page of this app, and never the sign-in page itself. */
export const safeReturnPath = (value: unknown): string => {
  if (typeof value !== 'string' || !value) return '/';
  try {
    const url = new URL(value, window.location.origin);
    if (url.origin !== window.location.origin || url.pathname === LOGIN_PATH) {
      return '/';
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return '/';
  }
};

/** The sign-in page's URL, remembering where to come back to. */
export const loginUrl = (returnTo: string): string => {
  const target = safeReturnPath(returnTo);
  return target === '/'
    ? LOGIN_PATH
    : `${LOGIN_PATH}?${RETURN_TO_PARAM}=${encodeURIComponent(target)}`;
};

/** Where a sign-in started from this page should land. */
export const signInReturnPath = (): string =>
  isLoginPage()
    ? safeReturnPath(
        new URLSearchParams(window.location.search).get(RETURN_TO_PARAM)
      )
    : safeReturnPath(isOidcCallback() ? '/' : currentPath());
